import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'app-components';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Dropdown from '../../../../lib/inputs/Dropdown';
import { useFormHandler } from '../hooks/useFormHandler';
import { ROOT_STATE } from '../../../../store/store.types';
import { getViewDetailUrl, handleChange } from '../constants/ticketlist.utils';
import { defaultOptions } from '../constants/ticketlist.constants';
import GIButton from '../../../../components/common/button/GIButton';
import { INPUT_TYPES } from '../../../../constants/inputs/inputTypes';
import { IReassignTicketModal } from '../constants/ticketlist.interface';
import { responseCheck, useApiHandler } from '../hooks/useApiHandler';
import { StyledError, StyledFlexCenter, StyledModal } from '../constants/ticketlist.styled';

export const ReassignTicketModal = ({ onClose }: IReassignTicketModal) => {
    const dispatch = useDispatch();
    const [options, setOptions] = useState<any>([]);
    const {
        modalData: { action, ticketId, reassignHeader, assignee: activeAssignee, taskId, queueId },
        allUsersForQueue,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { reassignTicketAPI } = useApiHandler();
    const { reassignTicketForm } = useFormHandler();
    const {
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = reassignTicketForm;

    const values = watch();

    useEffect(() => {
        options.map(option => {
            if (option.text === activeAssignee) {
                setValue('assignee', option.value);
            }
        });
    }, [activeAssignee, options]);

    useEffect(() => {
        if (Object.keys(allUsersForQueue).length === 0) {
            setOptions([{ text: 'Unassign', value: 'Automatic' }]);
        } else {
            setOptions([
                ...defaultOptions,
                ...Object.keys(allUsersForQueue).map(key => ({
                    value: key,
                    text: allUsersForQueue[key],
                })),
            ]);
        }
    }, [allUsersForQueue]);

    const onSubmit = useCallback(
        async (formData: any) => {
            const { assignee } = formData;
            const payload = {
                taskId: taskId,
                queueName: queueId,
                assignee,
                ticketId,
                action,
                csrftoken: window.csrf_token,
            };
            const response = await reassignTicketAPI(dispatch, payload);
            if (responseCheck(response)) {
                onClose();
                window.location.href = getViewDetailUrl(ticketId);
            }
        },
        [taskId, queueId, activeAssignee, ticketId, action],
    );

    return (
        <StyledModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal id="reassignTicketModal" classNames="reassign-ticket-modal" data-testid="reassign-ticket-modal">
                    <Modal.Header
                        id="reassignTicketModalHeader"
                        data-testid="reassign-ticket-modal-header"
                        titleId={reassignHeader}
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="reassign-ticket-modal-body">
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.modal.reassignDropdown.label" />,
                                labelClass: ['dropdown-label'],
                            }}
                            inputData={{
                                inputClass: ['dropdown-input'],
                                name: 'assignee',
                                type: INPUT_TYPES.DROPDOWN,
                                value: values.assignee,
                                isInputRequired: true,
                                options,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleChange('assignee', value, setValue),
                            }}
                        />
                        <StyledFlexCenter>
                            <StyledError data-testid="reassign-ticket-modal-error">
                                {errors?.assignee?.message}
                            </StyledError>
                        </StyledFlexCenter>
                    </Modal.Body>
                    <Modal.Controls data-testid="reassign-ticket-modal-controls">
                        <GIButton
                            id="reassignTicketModalCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                        <GIButton
                            id="reassignTicketModalSaveButton"
                            type="submit"
                            variant="primary"
                            labelId="ticketList.modal.reassignTicketModal.button.update"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledModal>
    );
};
